import { type CustomFieldValue as CustomFieldValueEntity } from '@webapp/core/entities/internal/CustomField'
import { type CustomFieldValue } from '@webapp/models'
import { COLUMN_TYPE_DEFAULTS } from '../config/DataTable.config'
import {
  type ColumnType,
  type DatatableColumnConfig,
} from '../types/DataTable.types'

export const configureColumn = <DataType, CT extends ColumnType>(
  columnType: CT,
  config: Omit<
    DatatableColumnConfig<CT, DataType>,
    'type' | 'BodyCellComponent'
  >
): DatatableColumnConfig<CT, DataType> => {
  return {
    type: columnType,
    defaultVisible: true,
    locked: false,
    loaderWidth: 60,
    ...(COLUMN_TYPE_DEFAULTS[columnType] || {}),
    ...config,
  } as DatatableColumnConfig<CT, DataType>
}

/**
 *
 * @param customfieldValues The custom field values as returned in the items in the data list.
 * This can just be the custom field values of the first item on the list. We need this to build the
 * custom field columns, as we can derive all of the custom field data from the custom field values (ie. customfield.field).
 * This assumes that the custom field values array in each item in the data list are consistently ordered.
 * @param cfGetter The getter function that gets the custom field values array from our data
 * @returns DatatableColumnConfig[]
 */
export const configureCFColumns = <DataType,>(
  customfieldValues: CustomFieldValue[] | CustomFieldValueEntity[],
  cfGetter: (data: DataType) => CustomFieldValue[] | CustomFieldValueEntity[],
  config?: Partial<
    Omit<
      DatatableColumnConfig<'CUSTOM_FIELD', DataType>,
      'type' | 'BodyCellComponent'
    >
  >
): DatatableColumnConfig<'CUSTOM_FIELD', DataType>[] => {
  if (!customfieldValues) return []

  return customfieldValues.map((cfValue, cfColIndex) => {
    return {
      type: 'CUSTOM_FIELD',
      label: cfValue.field.name,
      defaultVisible: true,
      locked: false,
      loaderWidth: 60,
      ...COLUMN_TYPE_DEFAULTS.CUSTOM_FIELD,
      getter: (data) => {
        return cfGetter(data)[cfColIndex]
      },
      ...config,
    } as DatatableColumnConfig<'CUSTOM_FIELD', DataType>
  })
}
